import React, { useEffect, useState, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { OWNERSHIPTYPES } from '../../components/constants'
import api from '../../utils/api'
import { ErrorNotification, SuccessNotification } from '../../utils'
import { FaRegCheckCircle, FaTrash, FaUpload } from 'react-icons/fa'
import { IoIosArrowRoundBack } from 'react-icons/io'
import { useHistory, useLocation } from "react-router-dom"
import { trimFormValue } from '../../utils/formValues'
import { useLocationCascade } from '../../hooks/useLocations'

const trimField = { setValueAs: trimFormValue }

const UpdateFarmView = () => {
  const location = useLocation()
  const history = useHistory()
  const [loading, setLoading] = useState(false)
  const [farm, setFarm] = useState({
    farm_name: '',
    farm_size: '',
    farm_region: '',
    farm_district: '',
    farm_community: '',
    primary_crop: '',
    longitude: '',
    latitude: '',
    ownership: '',
    other_crops: [],
    farm_images: [],
    farm_documents: [],
  })
  const [crops, setCrops] = useState([])
  const [existingImages, setExistingImages] = useState([])
  const submittingRef = useRef(false)

  const { register, handleSubmit, setValue, formState: { errors } } = useForm({
    defaultValues: farm
  })

  const {
    regionNames,
    districts: farmDistricts,
    communities: farmCommunities,
  } = useLocationCascade(farm.farm_region, farm.farm_district)

  useEffect(() => {
    api.get('commodities/').then(res => {
      if (res.status === 200 && res.data) setCrops(res.data)
    })
    if (location.state && location.state.farm) {
      const f = location.state.farm
      const prefill = {
        id: f.id || '',
        farm_name: f.farm_name || '',
        farm_size: f.farm_size ? String(f.farm_size) : '',
        farm_region: f.region || '',
        farm_district: f.district || '',
        farm_community: f.community || '',
        primary_crop: f.primary_crop && typeof f.primary_crop === 'object'
          ? (f.primary_crop.id || f.primary_crop.name || '')
          : (f.primary_crop || ''),
        longitude: f.longitude ? String(f.longitude) : null,
        latitude: f.latitude ? String(f.latitude) : null,
        ownership: f.ownership_type || '',
        other_crops: Array.isArray(f.crop_types)
          ? f.crop_types.map(crop => crop.id || crop.name)
          : [],
        farm_images: [],
        farm_documents: [],
      }
      setFarm(prefill)
      setExistingImages(Array.isArray(f.images) ? f.images.map(img => img.image) : [])
      // Sync with react-hook-form
      Object.entries(prefill).forEach(([key, value]) => {
        setValue(key, value, { shouldValidate: true })
      })
    }
  }, [location.state])

  const handleFarmImageChange = (e) => {
    const files = Array.from(e.target.files || [])
    setFarm(prev => ({ ...prev, farm_images: [...prev.farm_images, ...files] }))
  }

  const handleFarmDocumentChange = (e) => {
    const files = Array.from(e.target.files || [])
    setFarm(prev => ({ ...prev, farm_documents: [...prev.farm_documents, ...files] }))
  }

  const removeExistingImage = (idx) => {
    setExistingImages(existingImages.filter((_, i) => i !== idx))
  }
  const removeFarmImage = (imgIdx) => {
    setFarm({
      ...farm,
      farm_images: farm.farm_images.filter((_, i) => i !== imgIdx),
    })
  }

  const removeFarmDocument = (docIdx) => {
    setFarm(prev => ({ ...prev, farm_documents: prev.farm_documents.filter((_, i) => i !== docIdx) }))
  }

  const toggleFarmCrop = (cropId) => {
    setFarm(prev => {
      const currentCrops = prev.other_crops
      return currentCrops.includes(cropId)
        ? { ...prev, other_crops: currentCrops.filter(id => id !== cropId) }
        : { ...prev, other_crops: [...currentCrops, cropId] }
    })
  }

  const onSubmit = async () => {
    if (submittingRef.current) return
    submittingRef.current = true
    setLoading(true)
    try {
      // PATCH main farm data
      const payload = {
        farm_id: farm.id || location.state?.id,
        id: farm.id || location.state?.id,
        farm_name: trimFormValue(farm.farm_name),
        farm_size: parseFloat(farm.farm_size),
        region: farm.farm_region,
        district: farm.farm_district,
        community: farm.farm_community,
        longitude: farm.longitude ? parseFloat(farm.longitude) : null,
        latitude: farm.latitude ? parseFloat(farm.latitude) : null,
        primary_crop: farm.primary_crop,
        ownership_type: farm.ownership,
        crop_type_ids: farm.other_crops,
      }
      const farmerId = farm.farmer?.id || (location.state?.farm?.farmer?.id)
      await api.patch(
        `farmers/${farmerId}/update_farm/`,
        JSON.stringify(payload),
        { headers: { 'Content-Type': 'application/json' } }
      )
      // Upload new images/docs if any
      if (farm.farm_images.length > 0 || farm.farm_documents.length > 0) {
        const imageFormData = new FormData()
        const docsData = new FormData()


        farm.farm_images.forEach((file) => {
          imageFormData.append('images', file)
        })

        docsData.append('farm_document', farm.farm_documents[0])

        imageFormData.append('farm_id', farm.id)
        docsData.append('farm_id', farm.id)


        await api.post(
          `farmers/${farmerId}/upload_farm_images/`,
          imageFormData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )

        if (farm.farm_documents?.length > 0) {
          await api.patch(
            `/farmers/${farmerId}/update_farm/`,
            docsData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
          )
        }
      }
      setLoading(false)
      submittingRef.current = false
      SuccessNotification('Farm updated successfully')
      history.goBack()
    } catch (e) {
      ErrorNotification('Failed to update farm')
      setLoading(false)
      submittingRef.current = false
    }
  }

  return (
    <div className="w-full lg:px-10 px-2 h-full py-6">
      <div className="flex items-center justify-between">
        <button type="button" onClick={() => history.goBack()} className="mb-3 flex flex-row items-center">
          <IoIosArrowRoundBack size={25} className="mr-2 dark:text-white" />
          <p className="text-sm font-light capitalize dark:text-white">
            Back
          </p>
        </button>
      </div>

      <h1 className="text-lg font-bold capitalize mb-5 dark:text-white">Update Farm</h1>
      <div className='px-4 py-2 mt-2 rounded-lg  bg-white dark:bg-dark'>
        <form className="" onSubmit={handleSubmit(onSubmit)}>
          <div className="p-4">
            <div className="flex flex-col space-y-4 md:grid md:grid-cols-2 md:gap-7">
              <div className="w-full space-y-3 mt-4">
                <label className="text-sm font-normal capitalize dark:text-white">Farm Name</label>
                <input type="text" placeholder="Enter your farm name" className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400" value={farm.farm_name} {...register('farm_name', { ...trimField, required: 'Farm name is required' })} onChange={e => { setFarm({ ...farm, farm_name: e.target.value }); setValue('farm_name', e.target.value, { shouldValidate: true }) }} onBlur={e => { const trimmed = e.target.value.trim(); setFarm({ ...farm, farm_name: trimmed }); setValue('farm_name', trimmed, { shouldValidate: true }) }} />
                {errors.farm_name && <p className="text-red-500 text-xs mt-1">{errors.farm_name.message}</p>}
              </div>
              <div className="w-full space-y-3">
                <label className="text-sm font-normal capitalize dark:text-white">Farm Size (Acres)<span className="text-red-500">*</span></label>
                <input type="number" min={1} placeholder="Enter your farm size" className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400" value={farm.farm_size} {...register('farm_size', { required: 'Farm size is required' })} onChange={e => { setFarm({ ...farm, farm_size: e.target.value }); setValue('farm_size', e.target.value, { shouldValidate: true }) }} />
                {errors.farm_size && <p className="text-red-500 text-xs mt-1">{errors.farm_size.message}</p>}
              </div>
              <div className="w-full space-y-3">
                <label className="text-sm font-normal capitalize dark:text-white">Region<span className="text-red-500">*</span></label>
                <select className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400" value={farm.farm_region} {...register('farm_region', { required: 'Region is required' })} onChange={e => { setFarm({ ...farm, farm_region: e.target.value, farm_district: '', farm_community: '' }); setValue('farm_region', e.target.value, { shouldValidate: true }); setValue('farm_district', '', { shouldValidate: true }); setValue('farm_community', '', { shouldValidate: true }) }}>
                  <option value="">Select Region</option>
                  {regionNames.map((regionName, idx) => <option key={idx} value={regionName}>{regionName}</option>)}
                </select>
                {errors.farm_region && <p className="text-red-500 text-xs mt-1">{errors.farm_region.message}</p>}
              </div>
              <div className="w-full space-y-3">
                <label className="text-sm font-normal capitalize dark:text-white">District<span className="text-red-500">*</span></label>
                <select className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400" value={farm.farm_district} {...register('farm_district', { required: 'District is required' })} onChange={e => { setFarm({ ...farm, farm_district: e.target.value, farm_community: '' }); setValue('farm_district', e.target.value, { shouldValidate: true }); setValue('farm_community', '', { shouldValidate: true }) }}>
                  <option value="">Select District</option>
                  {farmDistricts?.map((item, idx) => <option key={idx} value={item.name}>{item.name}</option>)}
                </select>
                {errors.farm_district && <p className="text-red-500 text-xs mt-1">{errors.farm_district.message}</p>}
              </div>
              <div className="w-full space-y-3">
                <label className="text-sm font-normal capitalize dark:text-white">Community<span className="text-red-500">*</span></label>
                <select className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400" value={farm.farm_community} {...register('farm_community', { required: 'Community is required' })} onChange={e => { setFarm({ ...farm, farm_community: e.target.value }); setValue('farm_community', e.target.value, { shouldValidate: true }) }}>
                  <option value="">Select Community</option>
                  {farmCommunities?.map((item, idx) => <option key={idx} value={item}>{item}</option>)}
                </select>
                {errors.farm_community && <p className="text-red-500 text-xs mt-1">{errors.farm_community.message}</p>}
              </div>
              <div className="w-full space-y-3">
                <label className="text-sm font-normal capitalize dark:text-white">Primary Commodity<span className="text-red-500">*</span></label>
                <select
                  name="primary_crop"
                  id="primary_crop"
                  className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400"
                  value={farm.primary_crop} {...register('primary_crop', { required: 'Primary crop is required' })} onChange={e => { setFarm({ ...farm, primary_crop: e.target.value }); setValue('primary_crop', e.target.value, { shouldValidate: true }) }}
                >
                  <option>Select Primary Commodity</option>
                  {crops?.map((item) => <option className="capitalize" value={item?.name}>{item?.name}</option>)}
                </select>
                {errors.primary_crop && <p className="text-red-500 text-xs mt-1">{errors.primary_crop.message}</p>}
              </div>
              <div className="w-full space-y-3">
                <label className="text-sm font-normal capitalize dark:text-white">Longitude</label>
                <input className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400" value={farm.longitude} {...register('longitude')} onChange={e => { setFarm({ ...farm, longitude: e.target.value }); setValue('longitude', e.target.value, { shouldValidate: true }) }} />
               
              </div>
              <div className="w-full space-y-3">
                <label className="text-sm font-normal capitalize dark:text-white">Latitude</label>
                <input className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400" value={farm.latitude} {...register('latitude')} onChange={e => { setFarm({ ...farm, latitude: e.target.value }); setValue('latitude', e.target.value, { shouldValidate: true }) }} />
                
              </div>
              <div className="w-full space-y-3">
                <label className="text-sm font-normal capitalize dark:text-white">Ownership Type<span className="text-red-500">*</span></label>
                <select
                  name="ownership"
                  id="ownership"
                  className="w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 focus:outline-none focus:ring-2 focus:ring-yellowcolor capitalize focus:border-transparent placeholder-gray-400"
                  value={farm.ownership}
                  {...register('ownership', { required: 'Ownership type is required' })}
                  onChange={e => {
                    setFarm({ ...farm, ownership: e.target.value })
                    setValue('ownership', e.target.value, { shouldValidate: true })
                  }}
                >
                  <option>Select Ownership Type</option>
                  {OWNERSHIPTYPES?.map((item) => <option className="capitalize" value={item}>{item}</option>)}
                </select>
                {errors.ownership && <p className="text-red-500 text-xs mt-1">{errors.ownership.message}</p>}
              </div>
              <div></div>
              <div className="w-full space-y-3 col-span-2">
                <label className="text-sm font-normal capitalize dark:text-white">Add Other Crops/ Commodities</label>
                <div className="flex flex-row items-center gap-4 flex-wrap">
                  {crops?.map((item, idx) => (
                    <button type="button" key={idx} className="bg-white dark:bg-dark border dark:border-border-dark dark:text-white rounded-full py-2 px-3 flex flex-row items-center gap-2 text-sm" onClick={() => toggleFarmCrop(item?.id)}>
                      <p className="text-black dark:text-white">{item?.name}</p>
                      {farm.other_crops.includes(item?.id) ? <FaRegCheckCircle className="w-4 h-4 text-yellowcolor" /> : <div className="w-4 h-4 border border-gray-500 rounded-full" />}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="w-full space-y-3 mt-5">
              <label className="text-sm font-normal capitalize dark:text-white">
                Upload photos (Farm Images)
              </label>
              <div className="bg-gray-100 h-52 flex items-center justify-center rounded-md p-4 dark:bg-secondaryDark">
                <label htmlFor="farm_images" className="cursor-pointer">
                  <div className="bg-white px-20 border border-dashed border-gray-200 rounded-md p-4 flex flex-col items-center justify-center gap-4 dark:bg-dark dark:border-border-dark">
                    <FaUpload size={20} className="text-yellowcolor" />
                    <p className="text-sm text-black dark:text-white font-light text-center">Browse or drag and drop farm photos you want <br /> to upload from your computer</p>
                    <p className="text-base text-yellowcolor underline text-center">Click to upload</p>
                  </div>
                  <input type="file" id="farm_images" className="hidden" accept="image/png,image/jpeg" multiple onChange={handleFarmImageChange} />
                </label>
              </div>
            </div>
            {(existingImages.length > 0 || farm.farm_images.length > 0) && (
              <div className="w-full mt-5">
                <div className="bg-gray-100 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 rounded-md p-4 dark:bg-secondaryDark">
                  {/* Existing images */}
                  {existingImages.map((url, idx) => (
                    <div key={idx} className="bg-white h-32 relative group rounded-md overflow-hidden">
                      <img src={url} alt="farm image" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeExistingImage(idx)}
                        className="absolute top-0 right-0 bg-black/50 text-red-500 p-2 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      >
                        <FaTrash className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {/* New uploads */}
                  {farm.farm_images.map((item, imgIdx) => (
                    <div key={imgIdx} className="bg-white h-32 relative group rounded-md overflow-hidden">
                      <img src={URL.createObjectURL(item)} alt="farm image" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeFarmImage(imgIdx)}
                        className="absolute top-0 right-0 bg-black/50 text-red-500 p-2 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      >
                        <FaTrash className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <hr className="my-5 border-gray-200" />
            <div className="w-full space-y-3">
              <label className="text-sm font-normal capitalize dark:text-white">Farm Documents (Optional)</label>
              <div className="bg-gray-100 h-52 flex items-center justify-center rounded-md p-4 dark:bg-secondaryDark">
                <label htmlFor="farm_documents" className="cursor-pointer">
                  <div className="bg-white px-20 border border-dashed border-gray-200 rounded-md p-4 flex flex-col items-center justify-center gap-4 dark:bg-dark dark:border-border-dark dark:border-border-dark">
                    <FaUpload size={20} className="text-yellowcolor" />
                    <p className="text-sm text-black dark:text-white font-light text-center">Browse or drag and drop farm documents.</p>
                    <p className="text-base text-yellowcolor underline text-center">Click to upload</p>
                  </div>
                  <input type="file" id="farm_documents" className="hidden" accept="application/pdf" multiple onChange={handleFarmDocumentChange} />
                </label>
              </div>
            </div>
            {farm.farm_documents.length > 0 && <div className="w-full mt-5">
              <div className="bg-gray-100 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 rounded-md p-4 dark:bg-secondaryDark">
                {farm.farm_documents.map((item, docIdx) => (
                  <div key={docIdx} className="bg-white h-32 relative group rounded-md overflow-hidden flex flex-col items-center justify-center p-2">
                    <div className="flex flex-col items-center justify-center w-full h-full">
                      <FaUpload size={20} className="text-yellowcolor" />
                      <span className="block text-xs text-center mt-2 truncate w-24" title={item.name}>{item.name}</span>
                    </div>
                    <a href={URL.createObjectURL(item)} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 underline mt-1">View</a>
                    <button type="button" onClick={() => removeFarmDocument(docIdx)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"><svg width="14" height="14" viewBox="0 0 20 20" fill="none"><path d="M6 6L14 14M6 14L14 6" stroke="white" strokeWidth="2" strokeLinecap="round" /></svg></button>
                  </div>
                ))}
              </div>
            </div>}
            <div className="h-8"></div>
            <div className="flex justify-end mt-8">
              <button type="submit"
                className='px-6 text-sm py-2 bg-yellowcolor text-black rounded-lg'
                disabled={loading}>{loading ? 'Updating...' : 'Update Farm'}</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default UpdateFarmView